import React, { useEffect, useState } from "react";
import { Dropdown, ResponsiveMode, TeachingBubble, Toggle } from "@fluentui/react";
import { TextField } from "@fluentui/react/lib/TextField";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { FullPageModalDropDown } from "../generic/FullPageModalDropDown";
import { WithApiFileLocationProvider } from "./WithApiFileLocationProvider";
import { FileLocationExplorer } from "./FileLocationExplorer";
import { mergeStyles, mergeStyleSets } from "@fluentui/react/lib/Styling";
import { FileLocation, SelectedItemLocation } from "./FileLocationProvider";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import { navigate } from "../../redux/feature/general/navigation-slice";
import { SETTINGS } from "../../utils/constants/navigation";
import { fetchExploredDocumentPartLocation } from "../../api/document/Location";
import { DocumentData, EditOperationType } from "./EditDocument";
import { fetchLanguages } from "../../api/document/Language";
import { Language } from "../../api/models/language";
import { tenantConfigurationState } from "../../redux/feature/settings/configurations-slice";
import { IconButton } from "@fluentui/react/lib/Button";

const validate = (input: DocumentData, ignoreLanguage: boolean, isEdit: boolean) => {
  if (!ignoreLanguage && !input.language) return false;
  if (isEdit) return true;
  return !!input.name?.length;
};

interface DocumentInfoProps {
  documentInfo: DocumentData;
  updateDocumentInfo: (doc: DocumentData, valid: boolean) => void;
  isEdit: boolean;
  removeLocale?: boolean;
  editDocumentOperationType?: EditOperationType;
  isDuplicate: boolean;
  askForAttachmentsDuplication?: boolean;
}

const className = mergeStyleSets({
  h6: {
    fontSize: "0.875rem",
    marginTop: "10px",
  },
  box: {
    width: "99%",
    height: "99%",
    position: "absolute",
    top: "0",
    left: "0",
    opacity: "0.6",
    zIndex: "1000",
    margin: "2px",
    backgroundColor: "gray",
  },
  a: {
    color: "var(--color-black)",
  },
});

const scrollableContentStyle = mergeStyleSets({
  scrollableContent: mergeStyles({
    maxHeight: "calc(100vh - 62px)",
  }),
});

function validateDocumentName(str: string): string | undefined {
  const regex = /[\\/:*?"<>|]/; // regular expression to match unwanted characters
  if (regex.test(str)) {
    return formatMessage("cof.createdocument.documentname.invalidChar"); // return error message
  }
  return undefined; // no error, return undefined
}

export const InfoDocumentPanel: React.FunctionComponent<DocumentInfoProps> = (props) => {
  const { documentInfo, updateDocumentInfo, isEdit, editDocumentOperationType, removeLocale, isDuplicate } = props;
  const configurationState = useAppSelector(tenantConfigurationState);
  const isDuplicateOrDetach =
    editDocumentOperationType === "DUPLICATE" ||
    editDocumentOperationType === "DETACH" ||
    editDocumentOperationType === "DETACHFAKE";

  const ignoreLocale = removeLocale || isDuplicateOrDetach;

  const [state, setState] = useState<{
    nameError: string | undefined;
    locations: SelectedItemLocation;
    isModalOpen: boolean;
    isBubbleOpen: boolean;
    language: Language[];
  }>({ nameError: undefined, locations: null, isModalOpen: false, isBubbleOpen: false, language: [] });

  useEffect(() => {
    fetchLanguages().then((language) => {
      setState((s) => ({ ...s, language }));
    });
  }, []);

  function onChangeDescription(newDescription) {
    const newData = { ...documentInfo, description: newDescription };
    updateDocumentInfo(newData, validate(newData, ignoreLocale, isEdit));
  }

  function onChangeDuplicateAttachments(duplicateAttachments: boolean) {
    const newData = { ...documentInfo, duplicateAttachments };
    updateDocumentInfo(newData, validate(newData, ignoreLocale, isEdit));
  }

  function onChangeLanguage(newLanguage: string) {
    const newData: DocumentData = {
      ...documentInfo,
      language: newLanguage,
    };
    updateDocumentInfo(newData, validate(newData, ignoreLocale, isEdit));
  }

  const onChangeDocumentName = (newName: string) => {
    const newData = { ...documentInfo, name: newName };
    const nameError = validateDocumentName(newName);
    updateDocumentInfo(newData, validate(newData, ignoreLocale, isEdit) && !nameError);
    setState((s) => ({ ...s, nameError: nameError }));
  };

  const dispatch = useAppDispatch();

  function onSelectFolderPath(locations: SelectedItemLocation) {
    setState((s) => ({ ...s, isModalOpen: false, locations: locations }));
    const newData = { ...documentInfo, path: locations?.fileLocationPath ?? "" };
    updateDocumentInfo(newData, validate(newData, ignoreLocale, isEdit));
  }

  function onCancelFolder() {
    setState((s) => ({ ...s, isModalOpen: false, locations: null }));
  }

  function goToSettings() {
    dispatch(navigate(SETTINGS));
  }

  const fetchItems = async (pathToFetch) => {
    const folders = await fetchExploredDocumentPartLocation(pathToFetch);
    return folders.map<FileLocation>((df) => ({
      key: df.name,
      text: df.name,
    }));
  };

  const infoButtonId = "infoButtonId";

  return (
    <>
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
          <TextField
            data-testid="idDocName"
            label={formatMessage("cof.createdocument.documentname")}
            required
            placeholder={formatMessage("cof.createdocument.enterdocumentname")}
            value={documentInfo?.name?.replace(/\.docx$/, "") ?? ""}
            onChange={(e) => onChangeDocumentName((e.target as HTMLInputElement).value)}
            errorMessage={state.nameError}
            disabled={
              props.editDocumentOperationType === "DETACHFAKE" || props.editDocumentOperationType === "IMPORTNONTAGETIK"
            }
          />
          {!isDuplicateOrDetach && (
            <TextField
              data-testid="idDocDescription"
              label={formatMessage("cof.base.additionalinfo")}
              placeholder={formatMessage("cof.base.additionalinfo")}
              value={documentInfo?.description ?? ""}
              onChange={(e) => onChangeDescription((e.target as HTMLInputElement).value)}
            />
          )}
        </div>
      </div>
      {isEdit || !documentInfo ? null : (
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <FullPageModalDropDown
              label={formatMessage("cof.createdocument.location")}
              placeholder={formatMessage("cof.createdocument.shareddocuments")}
              options={[]}
              modalTitle={formatMessage("cof.createdocument.choosefilelocation")}
              isOpen={state.isModalOpen}
              scrollableContentStyle={scrollableContentStyle.scrollableContent}
              selectedValue={documentInfo.path}
              disabled={props.editDocumentOperationType === "IMPORTNONTAGETIK"}
            >
              {isDuplicate ? (
                <div
                  style={{
                    display: "flex",
                    color: "var(--color-grey)",
                    fontSize: "14px",
                    padding: "5px",
                  }}
                >
                  {formatMessage("cof.duplicate.folderinfo")}
                </div>
              ) : null}
              <WithApiFileLocationProvider
                cleanState={documentInfo.path == null}
                selectedItem={state.locations}
                getItems={fetchItems}
                selectDepth="FOLDER"
              >
                <FileLocationExplorer onSelectItem={onSelectFolderPath} onCancelSelection={onCancelFolder} />
              </WithApiFileLocationProvider>
            </FullPageModalDropDown>
          </div>
        </div>
      )}
      {!ignoreLocale && documentInfo && (
        <>
          {state.isBubbleOpen && (
            <TeachingBubble
              calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
              target={`#${infoButtonId}`}
              hasCloseButton={true}
              closeButtonAriaLabel="Close"
              onDismiss={() => setState((s) => ({ ...s, isBubbleOpen: false }))}
              headline={formatMessage("cof.base.cpmlanguage")}
            >
              <text>{formatMessage("cof.createnewdocument.cpmlanguage.info.message")}</text>
            </TeachingBubble>
          )}
          <Dropdown
            id="language_dropdown"
            data-testid={"language"}
            label={formatMessage("cof.base.cpmlanguage")}
            placeholder={formatMessage("cof.base.select")}
            onRenderLabel={(dropProps) => (
              <div style={{ display: "flex", width: "100%", alignItems: "center" }}>
                {dropProps.label}
                {dropProps.required && (
                  <span
                    style={{
                      color: "rgb(164, 38, 44)",
                      whiteSpaceCollapse: "break-spaces",
                    }}
                  >
                    {" *"}
                  </span>
                )}
                <IconButton
                  id={"infoButtonId"}
                  aria-label="Info"
                  iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
                  onClick={() => setState((s) => ({ ...s, isBubbleOpen: true }))}
                />
              </div>
            )}
            required
            options={state.language.map((l) => ({ key: l.code, text: l.desc }))}
            onChange={(_, option) => onChangeLanguage(option.key.toString())}
            selectedKey={documentInfo.language}
            responsiveMode={ResponsiveMode.large}
          />
        </>
      )}
      {isDuplicateOrDetach && (
        <h6 className={className.h6}>{formatMessage("cof.documentmanagement.outline.duplicate.info.directory")}</h6>
      )}
      {!isEdit && !isDuplicateOrDetach && editDocumentOperationType !== "IMPORTNONTAGETIK" && (
        <>
          <h6 className={className.h6}>
            {formatMessageWithParams("cof.createnewdocument.infotenant", {
              DISPLAY_NAME: configurationState.selected,
            })}
          </h6>
          <h6 className={className.h6}>
            {formatMessage("cof.createnewdocument.infotenant.message")}{" "}
            <a href="#" onClick={() => goToSettings()}>
              {formatMessage("cof.createnewdocument.infotenant.connectionmanagement")}
            </a>
          </h6>
        </>
      )}
      {props.askForAttachmentsDuplication && (
        <div className="ms-sm12 ms-md12 ms-lg12">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
            <div>
              {formatMessage("cof.base.microsoftexcelsources")}
              <br />
              {formatMessage("cof.documentmanagement.outline.duplicate.duplicateattachments")}
            </div>
            <Toggle
              styles={{ root: { marginBottom: 0 } }}
              checked={documentInfo?.duplicateAttachments}
              onChange={(_event, duplicateAttachments?: boolean) => onChangeDuplicateAttachments(duplicateAttachments)}
            />
          </div>
        </div>
      )}
    </>
  );
};
